import React, { useEffect, useRef, useState } from 'react';
import {
  autoUpdate,
  inline,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';
import { useIsMobile } from '../../containers/Filter/hooks/useIsMobile';
import DropdownFilterButton from './DropdownFilter.Button';
import DropdownFilterContent from './DropdownFilter.Content';
import { DropdownFilterProps } from './types';

function DropdownFilter({
  name,
  heading,
  title,
  options,
  selectedValues,
  onSelectionChange,
  onClear,
  loading = false,
  error,
  trackId,
}: DropdownFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useIsMobile();

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<(HTMLElement | null)[]>([]);
  const { refs, floatingStyles, context, update } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({
        mainAxis: 4,
        crossAxis: isOpen && selectedValues.length > 0 ? 23 : 0,
      }),
      shift({ padding: 8 }),
      inline(),
    ],
    placement: 'bottom-start',
    strategy: 'fixed',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { enabled: !isMobile });
  const role = useRole(context, { role: 'listbox' });
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    loop: true,
    focusItemOnOpen: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, role, listNavigation]
  );

  useEffect(() => {
    if (isOpen && update) {
      update();
    }
  }, [isOpen, update, options, selectedValues]);

  const handleCheckboxChange = (value: string | number) => {
    const updatedSelection = selectedValues.includes(value)
      ? selectedValues.filter((selectedValue) => selectedValue !== value)
      : [...selectedValues, value];

    onSelectionChange(updatedSelection);
  };

  const handleClose = () => {
    setIsOpen(false);
    // restore focus to the dropdown button
    (refs.domReference.current as HTMLElement)?.focus();
  };

  return (
    <>
      <DropdownFilterButton
        isOpen={isOpen}
        title={title}
        selectedValues={selectedValues}
        setReference={refs.setReference}
        referenceProps={getReferenceProps()}
        trackId={trackId}
      />

      <DropdownFilterContent
        ui={{
          isMobile,
          setFloating: refs.setFloating,
          floatingStyles: floatingStyles,
          floatingProps: getFloatingProps(),
          itemProps: getItemProps(),
        }}
        data={{
          heading,
          name,
          options,
          selectedValues,
          listRef,
        }}
        state={{
          isOpen,
          loading,
          error,
          activeIndex,
        }}
        callbacks={{
          onCheckboxChange: handleCheckboxChange,
          onClear,
          onClose: handleClose,
        }}
      />
    </>
  );
}

export default DropdownFilter;
